// Garmin Connect controls: the profile's "Connected apps" row and a route's "Send to Garmin" button.
import { useTranslation } from 'react-i18next';
import type { GarminError } from './garmin';
import { useConnectGarmin, useDisconnectGarmin, useGarminStatus, useSendToGarmin } from './garmin';
import styles from './Garmin.module.css';

/** Profile → Connected apps: Garmin Connect with Connect/Disconnect. */
export function GarminConnection() {
  const { t } = useTranslation();
  const status = useGarminStatus();
  const connect = useConnectGarmin();
  const disconnect = useDisconnectGarmin();
  const busy = connect.isPending || disconnect.isPending;
  const connected = status.data?.connected === true;
  const error = connect.error ?? disconnect.error;

  return (
    <section className={styles.connection} aria-labelledby="connected-apps">
      <h3 id="connected-apps" className={styles.heading}>
        {t('garmin.connectedApps')}
      </h3>
      <div className={styles.appRow}>
        <span className={styles.appName}>
          Garmin Connect
          <span className={`${styles.state} ${connected ? styles.stateOn : ''}`}>
            {status.isPending ? '…' : connected ? t('garmin.connected') : t('garmin.notConnected')}
          </span>
        </span>
        {status.data &&
          (connected ? (
            <button
              type="button"
              className={styles.button}
              disabled={busy}
              onClick={() => disconnect.mutate()}
            >
              {disconnect.isPending ? t('garmin.disconnecting') : t('garmin.disconnect')}
            </button>
          ) : (
            <button
              type="button"
              className={styles.button}
              disabled={busy || !status.data.subscriber}
              onClick={() => connect.mutate()}
            >
              {connect.isPending ? t('garmin.connecting') : t('garmin.connect')}
            </button>
          ))}
      </div>
      {status.data && !connected && !status.data.subscriber && (
        <p className={styles.note}>{t('garmin.subscribersOnly')}</p>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {errorText(t, error)}
        </p>
      )}
    </section>
  );
}

/** On an open route's card: send it to Garmin Connect as a course (only when the account is linked). */
export function SendToGarmin({ routeGuid }: { routeGuid: string }) {
  const { t } = useTranslation();
  const status = useGarminStatus();
  const send = useSendToGarmin(routeGuid);
  if (!status.data?.connected) return null;

  return (
    <div className={styles.send}>
      <button type="button" className={styles.button} disabled={send.isPending} onClick={() => send.mutate()}>
        <GarminIcon />
        {send.isPending ? t('garmin.sending') : t('garmin.send')}
      </button>
      {send.isSuccess && (
        <p role="status" className={styles.success}>
          {t('garmin.sent')}
        </p>
      )}
      {send.isError && (
        <p role="alert" className={styles.error}>
          {errorText(t, send.error)}
        </p>
      )}
    </div>
  );
}

function errorText(t: (key: string) => string, error: GarminError) {
  switch (error.code) {
    case '1050':
      return t('garmin.subscribersOnly');
    case '1051':
      return t('garmin.errors.notConnected');
    case '1054':
      return t('garmin.errors.tooShort');
    default:
      return t('garmin.errors.failed');
  }
}

const GarminIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M12 16V4m0 0-4.5 4.5M12 4l4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" strokeLinecap="round" />
  </svg>
);
